import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { serverLogout, sendHeartbeat, stayConnected } from '../utils/auth';

const API = process.env.REACT_APP_BACKEND_URL;

// Événements considérés comme "activité utilisateur" (pas le trafic API de fond).
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'focus'];

// Clé localStorage partagée entre onglets (fallback au BroadcastChannel).
const LS_LAST_ACTIVITY = 'komerces_last_activity';
const BC_CHANNEL = 'komerces-idle';

// Chemins où le manager est passif (n'initie jamais de logout).
// Fiches publiques : /f/{token} et /p/{code} — un onglet fiche publique ne doit pas
// tuer la session interne (bug reproduit par Thie).
const isPublicPath = (pathname) =>
  pathname.startsWith('/f/') || pathname.startsWith('/p/') || pathname.startsWith('/shared/') || pathname === '/login';

/**
 * Manager d'inactivité — session unique cross-tabs.
 *
 * Cross-tab sync :
 *  - BroadcastChannel 'komerces-idle' (avec fallback storage event)
 *  - localStorage komerces_last_activity = timestamp cross-tab de dernière activité
 *  - Message 'activity' → tous les onglets réarment leur minuteur
 *  - Message 'stay-connected' → tous les onglets ferment leur modale + réarment
 *  - Message 'logout' → tous les onglets se déconnectent proprement ensemble
 *
 * Règle produit (validée Thie) :
 *  - Réveil onglet : on calcule l'inactivité RÉELLE (now − last_activity partagée)
 *  - > timeout → redirection directe /login, PAS de modale/countdown (ex. retour après 2h)
 *  - Dans fenêtre d'avertissement → modale avec temps restant réel
 *  - < timeout−warning → réarme normalement
 *
 * Avant tout logout, on vérifie la dernière activité partagée : si un autre onglet
 * vient de ranimer la session (< timeout), on annule le logout et on réarme.
 */
export const IdleTimeoutManager = () => {
  const navigate = useNavigate();
  const [warning, setWarning] = useState(false);
  const [countdown, setCountdown] = useState(60);

  // Toast "une autre session a été fermée"
  useEffect(() => {
    if (sessionStorage.getItem('komerces_prev_session_terminated') === '1') {
      sessionStorage.removeItem('komerces_prev_session_terminated');
      toast.warning('Une autre session a été fermée', {
        description:
          "Une autre session était active ailleurs, elle a été fermée. Si ce n'était pas vous, changez votre mot de passe immédiatement.",
        duration: 14000,
        action: {
          label: 'Changer le mot de passe',
          onClick: () => {
            sessionStorage.setItem('komerces_open_security', '1');
            navigate('/settings');
          },
        },
      });
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    // Route publique → manager passif : n'initie jamais de logout, écoute juste la synchro.
    const passive = isPublicPath(window.location.pathname);

    let warnTimer = null;
    let countdownInterval = null;
    let lastReset = Date.now();
    let lastHeartbeat = 0;
    let isWarning = false;
    let cancelled = false;
    let timeoutMin = 30;
    let warningSec = 60;

    // ─── Cross-tab sync setup ──────────────────────────────────
    let bc = null;
    try {
      if (typeof BroadcastChannel !== 'undefined') bc = new BroadcastChannel(BC_CHANNEL);
    } catch (_) { bc = null; }

    const readSharedActivity = () => {
      try {
        const v = parseInt(localStorage.getItem(LS_LAST_ACTIVITY) || '0', 10);
        return Number.isFinite(v) && v > 0 ? v : 0;
      } catch (_) { return 0; }
    };

    const writeSharedActivity = (ts) => {
      try { localStorage.setItem(LS_LAST_ACTIVITY, String(ts)); } catch (_) {}
    };

    const broadcast = (type, payload = {}) => {
      const msg = { type, ts: Date.now(), ...payload };
      if (bc) { try { bc.postMessage(msg); } catch (_) {} }
    };

    // Initialise la dernière activité partagée si absente OU périmée.
    // ⚠️ Fix iter91 : si l'horloge partagée est plus ancienne que le timeout, on considère
    // que c'est un résidu (login récent, autre session fermée à distance…) et on la refresh.
    // Sans ceci, un timestamp obsolète hérité d'une session précédente déclencherait
    // la modale d'inactivité IMMÉDIATEMENT au montage post-login.
    {
      const shared = readSharedActivity();
      if (!shared || (Date.now() - shared) > (timeoutMin * 60 * 1000)) {
        writeSharedActivity(Date.now());
      }
    }

    // ─── Timers ────────────────────────────────────────────────
    const clearTimers = () => {
      if (warnTimer) clearTimeout(warnTimer);
      if (countdownInterval) clearInterval(countdownInterval);
      warnTimer = null;
      countdownInterval = null;
    };

    const timeoutMs = () => timeoutMin * 60 * 1000;

    const elapsedSinceLastActivity = () => {
      // ⚠️ Source cross-tab UNIQUEMENT : lastReset est réinitialisé à Date.now() au montage,
      // il masquerait la vraie horloge partagée après un rechargement post-absence longue.
      // Le shared timestamp est la seule source fiable pour le wake-check et le garde-fou.
      const shared = readSharedActivity();
      return shared ? Date.now() - shared : 0;
    };

    const doLogout = async ({ silent = false, reason = 'inactivity' } = {}) => {
      clearTimers();
      isWarning = false;
      setWarning(false);
      // ⚠️ Avant toute déconnexion : re-vérifier l'activité partagée cross-tab.
      // Un autre onglet a peut-être ranimé la session pendant que ce timer courait.
      const elapsed = elapsedSinceLastActivity();
      if (elapsed < timeoutMs() && reason === 'inactivity') {
        // Un autre onglet est actif → annuler ce logout, réarmer
        armWarnTimer();
        return;
      }
      // Vrai logout : notifier les autres onglets
      broadcast('logout', { reason });
      await serverLogout();
      if (!silent) {
        toast.info('Session expirée après inactivité', {
          description: 'Reconnectez-vous pour reprendre votre travail.',
        });
      }
      navigate('/login', { replace: true });
    };

    const startCountdown = (initial = warningSec) => {
      isWarning = true;
      setWarning(true);
      let c = Math.max(1, Math.min(warningSec, Math.floor(initial)));
      setCountdown(c);
      countdownInterval = setInterval(() => {
        c -= 1;
        if (c <= 0) {
          doLogout({ reason: 'inactivity' });
          return;
        }
        setCountdown(c);
      }, 1000);
    };

    const armWarnTimer = () => {
      if (warnTimer) clearTimeout(warnTimer);
      if (passive) return; // route publique : jamais de warn/logout initié ici
      // Temps restant réel avant la fenêtre d'avertissement = timeout − warning − elapsed cross-tab
      const elapsed = elapsedSinceLastActivity();
      const untilWarnMs = Math.max(0, timeoutMs() - warningSec * 1000 - elapsed);
      warnTimer = setTimeout(() => startCountdown(), untilWarnMs);
    };

    // ─── Wake-up : check elapsed time REAL ─────────────────────
    // Sur retour d'onglet (visibilitychange visible) OU au montage : on regarde
    // combien de temps s'est réellement écoulé depuis la dernière activité partagée.
    const checkOnWake = () => {
      if (passive) return;
      const elapsedMs = elapsedSinceLastActivity();
      const tMs = timeoutMs();
      const warnMs = warningSec * 1000;
      if (elapsedMs > tMs) {
        // Retour après > timeout : session close, pas de rattrapage possible
        doLogout({ reason: 'inactivity_wake' });
        return;
      }
      if (elapsedMs > tMs - warnMs) {
        // Dans la fenêtre d'avertissement : afficher la modale avec le temps restant réel
        const remainingS = Math.max(1, Math.ceil((tMs - elapsedMs) / 1000));
        clearTimers();
        startCountdown(Math.min(remainingS, warningSec));
        return;
      }
      // Encore de la marge : réarmer normalement
      armWarnTimer();
    };

    // ─── Activity (local) ──────────────────────────────────────
    const onActivity = () => {
      if (isWarning) return; // pendant l'avertissement, seul "Je suis là" relance
      const now = Date.now();
      if (now - lastReset >= 2000) {
        lastReset = now;
        writeSharedActivity(now);
        broadcast('activity');
        armWarnTimer();
      }
      if (!passive && now - lastHeartbeat >= 60000) {
        lastHeartbeat = now;
        sendHeartbeat();
      }
    };

    // ─── "Je suis là" ──────────────────────────────────────────
    const onStay = async () => {
      // Sécurité : reclaim uniquement valide dans la fenêtre d'avertissement
      // (retour après des heures ne doit PAS ressusciter la session)
      const elapsed = elapsedSinceLastActivity();
      const tMs = timeoutMs();
      const warnMs = warningSec * 1000;
      if (elapsed > tMs) {
        // Session vraiment perdue → vraie déconnexion (pas de reclaim)
        doLogout({ reason: 'inactivity_wake' });
        return;
      }
      const res = await stayConnected();
      if (res.ok) {
        isWarning = false;
        setWarning(false);
        clearTimers();
        const now = Date.now();
        lastReset = now;
        lastHeartbeat = now;
        writeSharedActivity(now);
        broadcast('stay-connected');
        armWarnTimer();
        return;
      }
      if (res.status === 409) {
        clearTimers();
        isWarning = false;
        setWarning(false);
        broadcast('logout', { reason: 'session_elsewhere' });
        await serverLogout();
        toast.warning('Session ouverte ailleurs', {
          description: res.detail || 'Votre session a été ouverte sur un autre appareil ou navigateur.',
          duration: 10000,
        });
        navigate('/login', { replace: true });
        return;
      }
      // JWT expiré/invalide
      clearTimers();
      isWarning = false;
      setWarning(false);
      broadcast('logout', { reason: 'expired' });
      await serverLogout();
      toast.info('Session expirée', {
        description: 'Reconnectez-vous pour reprendre votre travail.',
      });
      navigate('/login', { replace: true });
    };
    window.addEventListener('idle-stay-connected', onStay);

    // ─── Handlers cross-tab ───────────────────────────────────
    const onBcMessage = (event) => {
      const msg = event?.data || {};
      if (msg.type === 'activity' || msg.type === 'stay-connected') {
        // Un autre onglet est actif : réarmer + fermer la modale locale si active
        lastReset = Math.max(lastReset, msg.ts || Date.now());
        if (isWarning) {
          isWarning = false;
          setWarning(false);
          clearTimers();
        }
        armWarnTimer();
      } else if (msg.type === 'logout') {
        // Un autre onglet déclenche un logout : suivre proprement (pas de re-broadcast)
        clearTimers();
        isWarning = false;
        setWarning(false);
        navigate('/login', { replace: true });
      }
    };
    if (bc) bc.onmessage = onBcMessage;

    // Fallback storage event (Safari ancien, etc.)
    const onStorage = (e) => {
      if (e.key === LS_LAST_ACTIVITY && e.newValue) {
        const ts = parseInt(e.newValue, 10) || 0;
        if (ts > lastReset) {
          lastReset = ts;
          if (isWarning) {
            isWarning = false;
            setWarning(false);
            clearTimers();
          }
          armWarnTimer();
        }
      } else if (e.key === 'token' && !e.newValue) {
        // Le token a été supprimé ailleurs (logout d'un autre onglet) : suivre
        clearTimers();
        isWarning = false;
        setWarning(false);
        navigate('/login', { replace: true });
      }
    };
    window.addEventListener('storage', onStorage);

    // Visibilitychange : au réveil de l'onglet, appliquer la règle du temps réel
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      checkOnWake();
    };
    document.addEventListener('visibilitychange', onVisibility);

    // Hook de test E2E (préview/dev)
    const onForceWarning = () => { if (!isWarning && !passive) startCountdown(); };
    const testHookEnabled = process.env.NODE_ENV !== 'production';
    if (testHookEnabled) window.addEventListener('idle-force-warning', onForceWarning);

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));

    // Heartbeat de secours : tant que l'onglet est VISIBLE, on signale l'activité au
    // serveur toutes les 60 s même sans event (lecture d'une longue fiche sans bouger
    // la souris). Sans ceci, `last_activity_at` devenait obsolète côté serveur et
    // l'utilisateur pourtant présent était déconnecté (cause « seul sur un appareil »,
    // iter127). L'autorité du logout reste le minuteur client (avertissement 60 s).
    const fallbackHeartbeat = setInterval(() => {
      if (passive || isWarning) return;
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastHeartbeat >= 60000) {
        lastHeartbeat = now;
        sendHeartbeat();
      }
    }, 30000);

    // Récupère la config puis check wake + arme (si non passif)
    fetch(`${API}/api/auth/session-config`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data) {
          timeoutMin = data.session_timeout_minutes || 30;
          warningSec = data.warning_seconds || 60;
        }
        lastHeartbeat = Date.now();
        // Au montage : vérifie si on est déjà en dehors du timeout (retour long)
        if (!passive) checkOnWake();
      })
      .catch(() => { if (!passive) checkOnWake(); });

    return () => {
      cancelled = true;
      clearTimers();
      clearInterval(fallbackHeartbeat);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      window.removeEventListener('idle-stay-connected', onStay);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
      if (testHookEnabled) window.removeEventListener('idle-force-warning', onForceWarning);
      if (bc) { try { bc.close(); } catch (_) {} }
    };
  }, [navigate]);

  if (!warning) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4"
      data-testid="idle-warning-overlay"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-7 text-center" data-testid="idle-warning-modal">
        <div className="w-14 h-14 mx-auto rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
          <i className="bi bi-clock-history text-2xl text-amber-600"></i>
        </div>
        <h2 className="text-lg font-bold text-slate-900">Vous allez être déconnecté</h2>
        <p className="text-sm text-slate-500 mt-2">
          Pour votre sécurité, votre session va se fermer dans
        </p>
        <div className="my-4">
          <span className="text-5xl font-bold tabular-nums text-slate-900" data-testid="idle-countdown">
            {countdown}
          </span>
          <span className="text-base text-slate-500 ml-1">s</span>
        </div>
        <p className="text-xs text-slate-400 mb-5">
          Cliquez ci-dessous pour rester connecté.
        </p>
        <button
          onClick={() => window.dispatchEvent(new Event('idle-stay-connected'))}
          className="w-full bg-[#0062CC] hover:bg-[#0056b3] text-white font-semibold py-3 rounded-xl transition flex items-center justify-center gap-2"
          data-testid="idle-stay-btn"
        >
          <i className="bi bi-hand-index-thumb"></i>
          Je suis là
        </button>
      </div>
    </div>
  );
};

export default IdleTimeoutManager;
